'use client';

import { motion } from 'motion/react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import RvIcon from '~/assets/vector/rv.svg';
import GenericFallback from '~/components/molecules/GenericFallback';
import MenuItem from '~/components/molecules/menu/MenuItem';
import { modelMenu } from '~/lib/api/models';
import routeGenerator from '~/lib/api/routes';
import { useHydrationSafeResponsive } from '~/lib/hooks/useResponsive';
import useScrollDirection from '~/lib/hooks/useScrollDirection';
import useScrollThreshold from '~/lib/hooks/useScrollThreshold';
import { boolAttr, cn } from '~/lib/utils/classname';

export interface Props {
  data?: z.infer<typeof modelMenu.schema>;
  isFallback?: boolean;
}

const MainMenuNav: React.FC<Props> = ({ data, isFallback }) => {
  const [isMobileOpen, setIsMobileOpen] = useState<boolean>(false);

  const hasData = !isFallback && data && data.menu.menuItems.edges;

  const pathname = usePathname();
  const isHomepage = pathname === '/';
  const isMobile = useHydrationSafeResponsive('mobile');
  const afterThreshold = useScrollThreshold(400);
  const scrollDirection = useScrollDirection();

  useEffect(() => {
    const currentFocusedElement = document.activeElement as HTMLElement | null;

    if (currentFocusedElement) {
      currentFocusedElement.blur();
    }
  }, [pathname]);

  useEffect(() => {
    if (!isMobile) {
      setIsMobileOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [pathname]);

  return (
    <div
      className="group/main-menu-nav pointer-events-none contents duration-1000 **:duration-[inherit]"
      data-mobile-open={boolAttr(isMobileOpen)}
      data-desktop-open={boolAttr(!isMobile && isHomepage && !afterThreshold)}
    >
      <div className="lg:group-hocus-within/main-menu-nav:pointer-events-auto pointer-events-none fixed top-0 left-0 z-40 px-6 py-7 mix-blend-difference group-data-mobile-open/main-menu-nav:pointer-events-auto max-lg:z-55 lg:px-24 lg:pt-24 lg:pb-0 lg:group-data-desktop-open/main-menu-nav:pointer-events-none lg:group-data-desktop-open/main-menu-nav:opacity-0">
        <button
          type="button"
          className="lg:group-hocus-within/main-menu-nav:pointer-events-none lg:group-hocus-within/main-menu-nav:opacity-0 pointer-events-auto relative -m-4 flex cursor-pointer flex-row items-center justify-center gap-4 p-4 opacity-100"
          aria-label={isMobileOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
          onClick={() => isMobile && setIsMobileOpen((value) => !value)}
        >
          <div className="relative flex aspect-square h-3 w-6.25 flex-col justify-between lg:w-8">
            <div
              className={cn(
                'pointer-events-none relative h-px w-full bg-white duration-300! lg:shadow-[0_1px_0] lg:shadow-white',
                {
                  'translate-y-[5.5px] rotate-45': isMobileOpen,
                }
              )}
            />

            <div
              className={cn(
                'pointer-events-none relative h-px w-full bg-white duration-300! lg:shadow-[0_-1px_0] lg:shadow-white',
                {
                  '-translate-y-[5.5px] -rotate-45': isMobileOpen,
                }
              )}
            />
          </div>

          <div className="relative flex overflow-clip">
            <motion.span
              className="inline-grid text-white transition-none!"
              initial={false}
              variants={{
                hidden: { y: '100%' },
                visible: { y: '0%' },
              }}
              transition={{
                type: 'tween',
                duration: 0.4,
                ease: 'easeOut',
              }}
              animate={scrollDirection === -1 ? 'visible' : 'hidden'}
            >
              <span className="col-start-1 row-start-1 opacity-100 transition-opacity duration-300 group-data-mobile-open/main-menu-nav:opacity-0">
                Menu
              </span>

              <span
                className="col-start-1 row-start-1 opacity-0 transition-opacity duration-300 group-data-mobile-open/main-menu-nav:opacity-100"
                aria-hidden
              >
                Fermer
              </span>
            </motion.span>
          </div>
        </button>
      </div>

      <nav className="fixed top-0 left-0 z-50 flex h-svh w-screen pt-20 lg:pt-0">
        <div className="pointer-events-none absolute top-0 left-0 -z-10 h-full w-full">
          <div className="lg:group-hocus-within/main-menu-nav:opacity-100 absolute inset-0 bg-linear-to-b from-white opacity-0 backdrop-blur-[3px] transition-opacity group-data-mobile-open/main-menu-nav:opacity-100 max-lg:via-white/75 lg:bg-linear-to-r lg:backdrop-blur-none lg:group-data-desktop-open/main-menu-nav:opacity-0!" />

          <div className="lg:group-hocus-within/main-menu-nav:opacity-100 gradient-mask-t-[transparent_0%,black_12.5%,black_25%,transparent_37.5%] lg:gradient-mask-l-[transparent_0%,black_12.5%,black_25%,transparent_37.5%] pointer-events-none absolute inset-0 z-1 opacity-0 backdrop-blur-[0.5px] transition-opacity group-data-mobile-open/main-menu-nav:opacity-100 lg:group-data-desktop-open/main-menu-nav:opacity-0!" />

          <div className="lg:group-hocus-within/main-menu-nav:opacity-100 gradient-mask-t-[transparent_12.5%,black_25%,black_37.5%,transparent_50%] lg:gradient-mask-l-[transparent_12.5%,black_25%,black_37.5%,transparent_50%] pointer-events-none absolute inset-0 z-2 opacity-0 backdrop-blur-[0.5625px] transition-opacity group-data-mobile-open/main-menu-nav:opacity-100 lg:group-data-desktop-open/main-menu-nav:opacity-0!" />

          <div className="lg:group-hocus-within/main-menu-nav:opacity-100 gradient-mask-t-[transparent_25%,black_37.5%,black_50%,transparent_62.5%] lg:gradient-mask-l-[transparent_25%,black_37.5%,black_50%,transparent_62.5%] pointer-events-none absolute inset-0 z-3 opacity-0 backdrop-blur-[1.125px] transition-opacity group-data-mobile-open/main-menu-nav:opacity-100 lg:group-data-desktop-open/main-menu-nav:opacity-0!" />

          <div className="lg:group-hocus-within/main-menu-nav:opacity-100 gradient-mask-t-[transparent_37.5%,black_50%,black_62.5%,transparent_75%] lg:gradient-mask-l-[transparent_37.5%,black_50%,black_62.5%,transparent_75%] pointer-events-none absolute inset-0 z-4 opacity-0 backdrop-blur-[2.25px] transition-opacity group-data-mobile-open/main-menu-nav:opacity-100 lg:group-data-desktop-open/main-menu-nav:opacity-0!" />

          <div className="lg:group-hocus-within/main-menu-nav:opacity-100 gradient-mask-t-[transparent_50%,black_62.5%,black_75%,transparent_87.5%] lg:gradient-mask-l-[transparent_50%,black_62.5%,black_75%,transparent_87.5%] pointer-events-none absolute inset-0 z-5 opacity-0 backdrop-blur-[4.5px] transition-opacity group-data-mobile-open/main-menu-nav:opacity-100 lg:group-data-desktop-open/main-menu-nav:opacity-0!" />

          <div className="lg:group-hocus-within/main-menu-nav:opacity-100 gradient-mask-t-[transparent_62.5%,black_75%,black_87.5%,transparent_100%] lg:gradient-mask-l-[transparent_62.5%,black_75%,black_87.5%,transparent_100%] pointer-events-none absolute inset-0 z-6 opacity-0 backdrop-blur-[9px] transition-opacity group-data-mobile-open/main-menu-nav:opacity-100 lg:group-data-desktop-open/main-menu-nav:opacity-0!" />

          <div className="lg:group-hocus-within/main-menu-nav:opacity-100 gradient-mask-t-[transparent_75%,black_87.5%,black_100%] lg:gradient-mask-l-[transparent_75%,black_87.5%,black_100%] pointer-events-none absolute inset-0 z-7 opacity-0 backdrop-blur-[18px] transition-opacity group-data-mobile-open/main-menu-nav:opacity-100 lg:group-data-desktop-open/main-menu-nav:opacity-0!" />

          <div className="lg:group-hocus-within/main-menu-nav:opacity-100 gradient-mask-t-[transparent_87.5%,black_100%] lg:gradient-mask-l-[transparent_87.5%,black_100%] pointer-events-none absolute inset-0 z-8 opacity-0 backdrop-blur-[36px] transition-opacity group-data-mobile-open/main-menu-nav:opacity-100 lg:group-data-desktop-open/main-menu-nav:opacity-0!" />
        </div>

        <div
          className="lg:group-hocus-within/main-menu-nav:pointer-events-auto pointer-events-none relative z-10 flex h-full w-screen flex-col justify-start gap-y-20 overflow-y-auto px-6 py-7 group-data-mobile-open/main-menu-nav:pointer-events-auto lg:h-svh lg:w-auto lg:justify-between lg:gap-y-0 lg:p-24 lg:group-data-desktop-open/main-menu-nav:pointer-events-auto"
          data-prevent="lenis"
        >
          {hasData ? (
            <ul className="lg:group-hocus-within/main-menu-nav:opacity-100 flex flex-col items-center gap-y-4 opacity-0 transition-opacity group-data-mobile-open/main-menu-nav:opacity-100 lg:items-start lg:group-data-desktop-open/main-menu-nav:opacity-100">
              {data.menu.menuItems.edges.map((item, index) => (
                <li
                  key={item.node.id}
                  className="lg:group-hocus-within/main-menu-nav:translate-y-0 lg:group-hocus-within/main-menu-nav:opacity-100 lg:group-hocus-within/main-menu-nav:delay- lg:group -data-[desktop-open]/main-menu-nav:translate-y-0 -translate-y-2 text-center opacity-0 transition-all delay-[0] group-data-mobile-open/main-menu-nav:translate-y-0 group-data-mobile-open/main-menu-nav:opacity-100 group-data-mobile-open/main-menu-nav:delay-(--delay) lg:group-data-desktop-open/main-menu-nav:opacity-100 lg:group-data-desktop-open/main-menu-nav:delay-(--delay)"
                  style={
                    { '--delay': `${index * 0.05}s` } as React.CSSProperties
                  }
                >
                  <MenuItem
                    item={item.node}
                    className="text-brand-primary text-sm font-normal max-lg:whitespace-nowrap lg:group-data-desktop-open/main-menu-nav:text-white"
                  />
                </li>
              ))}
            </ul>
          ) : (
            <div className="lg:group-hocus-within/main-menu-nav:opacity-100 flex flex-col gap-y-4 opacity-0 transition-opacity group-data-mobile-open/main-menu-nav:opacity-100 lg:group-data-desktop-open/main-menu-nav:opacity-100">
              <div className="lg:group-hocus-within/main-menu-nav:translate-y-0 lg:group-hocus-within/main-menu-nav:opacity-100 -translate-y-2 opacity-0 transition-all group-data-mobile-open/main-menu-nav:translate-y-0 group-data-mobile-open/main-menu-nav:opacity-100 lg:group-data-desktop-open/main-menu-nav:translate-y-0 lg:group-data-desktop-open/main-menu-nav:opacity-100">
                <GenericFallback variant="error" showIcon>
                  Error while rendering MainMenu
                </GenericFallback>
              </div>
            </div>
          )}

          <div className="text-brand-primary lg:group-hocus-within/main-menu-nav:opacity-100 lg:group-hocus-within/main-menu-nav:translate-y-0 -order-1 flex -translate-y-2 justify-center opacity-0 transition-all group-data-mobile-open/main-menu-nav:translate-y-0 group-data-mobile-open/main-menu-nav:opacity-100 lg:order-last lg:justify-start lg:group-data-desktop-open/main-menu-nav:translate-y-0 lg:group-data-desktop-open/main-menu-nav:opacity-0!">
            <Link href={routeGenerator.homepage()} aria-label="Accueil">
              <RvIcon className="w-6" />
            </Link>
          </div>
        </div>
      </nav>
    </div>
  );
};

export default MainMenuNav;
